/**
 * What the send-queue sheet says about a moment on its way out (FEAT-037).
 *
 * Copy, kept apart from the sheet that renders it, because these two functions
 * are the whole promise the feature makes: that a person can read what is
 * happening without knowing anything about queues. Brand voice — warm, plain,
 * a "moment" and never a "snap" (design/BRAND.md).
 */
import type { Destination, QueueRow } from '../services/queue/types';

/** Who or where, in the name he knows them by. */
export function destinationName(
  dest: Destination,
  friendName: (id: number) => string | undefined,
  groupName: (id: number) => string | undefined
): string {
  switch (dest.kind) {
    case 'friend':
      return friendName(dest.friendId) ?? 'a friend';
    case 'group':
      return groupName(dest.groupId) ?? 'your group';
    case 'story':
      return 'your story';
    case 'vault':
      return dest.isPrivate ? 'Only Me' : 'Memories';
  }
}

/**
 * What is happening to this row, in the words a person would use.
 *
 * The ORDER is the honesty. A row that needs a decision is not "waiting for
 * signal"; a row waiting on a sign-in has not failed (BUG-069); and a
 * percentage only appears while bytes are genuinely moving — the caller passes
 * the server's committed offset or nothing at all, never an estimate (BUG-058).
 */
export function rowSentence(
  row: QueueRow,
  where: string,
  offline: boolean,
  percent: number | null
): string {
  const verb = row.dest.kind === 'vault' ? 'Saving to' : 'Sending to';
  if (row.state === 'stuck') return 'Stuck — needs you';
  if (row.pausedAuth) return 'Waiting for sign-in';
  if (offline) return 'Waiting for signal';
  if (row.state === 'uploaded' || row.state === 'sending') return 'Almost there';
  if (row.state === 'uploading' && percent !== null) {
    return `${verb} ${where} · ${percent}%`;
  }
  return `${verb} ${where}`;
}
